import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  Col,
  Form,
  Input,
  message,
  Modal,
  Pagination,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  InboxOutlined,
  ReloadOutlined,
  SearchOutlined,
  ShopOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import {
  formatSubscriptionDate,
  formatSubscriptionNumber,
} from "./subscriptionFormatters";
import "./SubscriptionRequestsModal.css";

const { Paragraph, Text } = Typography;
const { TextArea } = Input;
const SEARCH_DELAY_MS = 320;

type RequestStatus = "pending" | "processed" | "rejected" | "cancelled";

interface SubscriptionRequest {
  id: number;
  company_id: number;
  company_name: string;
  company_email: string | null;
  request_type: "plan_change" | "renewal";
  requested_duration_months: number | null;
  message: string | null;
  status: RequestStatus;
  review_note: string | null;
  created_at: string;
  current_plan_name: string | null;
  requested_plan_name: string | null;
  requested_by_name: string | null;
  requested_by_email: string | null;
}

interface RequestStats {
  total: number;
  pending: number;
  processed: number;
  rejected: number;
  cancelled: number;
}

interface PaginationInfo {
  current_page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

interface SubscriptionRequestsModalProps {
  open: boolean;
  onClose: () => void;
  onUpdated?: () => void;
}

const EMPTY_STATS: RequestStats = {
  total: 0,
  pending: 0,
  processed: 0,
  rejected: 0,
  cancelled: 0,
};

const statusLabels: Record<RequestStatus, string> = {
  pending: "En attente",
  processed: "Traitée",
  rejected: "Refusée",
  cancelled: "Annulée",
};

const statusColors: Record<RequestStatus, string> = {
  pending: "processing",
  processed: "success",
  rejected: "error",
  cancelled: "default",
};

const SubscriptionRequestsModal: React.FC<SubscriptionRequestsModalProps> = ({
  open,
  onClose,
  onUpdated,
}) => {
  const [requests, setRequests] = useState<SubscriptionRequest[]>([]);
  const [stats, setStats] = useState<RequestStats>(EMPTY_STATS);
  const [pagination, setPagination] = useState<PaginationInfo>({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [selectedRequest, setSelectedRequest] =
    useState<SubscriptionRequest | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [form] = Form.useForm();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("Admin.Souscription.manage");

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedSearch(searchText.trim()),
      searchText ? SEARCH_DELAY_MS : 0,
    );
    return () => window.clearTimeout(timer);
  }, [searchText]);

  const loadRequests = useCallback(
    async (page = 1, limit = 10) => {
      if (!open) return;

      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
          status: statusFilter,
        });
        if (debouncedSearch) params.set("search", debouncedSearch);

        const response = await fetch(`/api/subscription/requests?${params}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        });
        const payload = await response.json();
        if (!response.ok || !payload.success) {
          throw new Error(payload.message || "Impossible de charger les demandes");
        }

        setRequests(payload.data.requests || []);
        setStats({ ...EMPTY_STATS, ...(payload.data.stats || {}) });
        setPagination(payload.data.pagination);
      } catch (error) {
        message.error(
          error instanceof Error
            ? error.message
            : "Impossible de charger les demandes",
        );
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearch, open, statusFilter],
  );

  useEffect(() => {
    loadRequests(1, pagination.per_page);
    // La taille de page est conservée, le changement de filtre repart en page 1.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRequests]);

  const reviewRequest = useCallback(
    async (status: "processed" | "rejected") => {
      if (!selectedRequest) return;

      const values = await form.validateFields();
      setReviewing(true);
      try {
        const response = await fetch(
          `/api/subscription/requests/${selectedRequest.id}/status`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              status,
              review_note: values.review_note,
            }),
          },
        );
        const payload = await response.json();
        if (!response.ok || !payload.success) {
          throw new Error(payload.message || "Impossible de traiter la demande");
        }

        message.success(payload.message);
        setSelectedRequest(null);
        form.resetFields();
        await loadRequests(pagination.current_page, pagination.per_page);
        onUpdated?.();
      } catch (error) {
        message.error(
          error instanceof Error
            ? error.message
            : "Impossible de traiter la demande",
        );
      } finally {
        setReviewing(false);
      }
    },
    [form, loadRequests, onUpdated, pagination, selectedRequest],
  );

  const columns = useMemo(
    () => [
      {
        title: "Entreprise",
        key: "company",
        width: 250,
        render: (_: unknown, request: SubscriptionRequest) => (
          <div className="subscription-request__company">
            <Avatar icon={<ShopOutlined />} />
            <div>
              <strong>{request.company_name}</strong>
              <span>{request.company_email || "Aucun e-mail renseigné"}</span>
            </div>
          </div>
        ),
      },
      {
        title: "Demande",
        key: "request",
        width: 280,
        render: (_: unknown, request: SubscriptionRequest) => (
          <div className="subscription-request__subject">
            <strong>
              {request.request_type === "plan_change"
                ? `Changement vers ${request.requested_plan_name || "un plan"}`
                : `Renouvellement de ${request.requested_duration_months} mois`}
            </strong>
            <span>
              {request.request_type === "plan_change"
                ? `Plan actuel : ${request.current_plan_name || "non attribué"}`
                : request.message || "Aucune précision ajoutée"}
            </span>
          </div>
        ),
      },
      {
        title: "Demandeur",
        key: "requester",
        render: (_: unknown, request: SubscriptionRequest) => (
          <div className="subscription-request__requester">
            <strong>{request.requested_by_name || "Utilisateur"}</strong>
            <span>{request.requested_by_email || "—"}</span>
          </div>
        ),
      },
      {
        title: "Date",
        dataIndex: "created_at",
        key: "created_at",
        render: formatSubscriptionDate,
      },
      {
        title: "Statut",
        dataIndex: "status",
        key: "status",
        render: (status: RequestStatus) => (
          <Tag color={statusColors[status]}>{statusLabels[status]}</Tag>
        ),
      },
      {
        title: "",
        key: "actions",
        fixed: "right" as const,
        width: 110,
        render: (_: unknown, request: SubscriptionRequest) =>
          request.status === "pending" && canManage ? (
            <Button
              type="primary"
              ghost
              onClick={() => {
                form.resetFields();
                setSelectedRequest(request);
              }}
            >
              Examiner
            </Button>
          ) : null,
      },
    ],
    [canManage, form],
  );

  return (
    <>
      <Modal
        centered
        open={open}
        onCancel={onClose}
        footer={null}
        width={1180}
        className="subscription-requests-modal"
        title={
          <div className="subscription-requests-modal__title">
            <span><InboxOutlined /></span>
            <div>
              <strong>Demandes des abonnés</strong>
              <small>Changements de plan et renouvellements</small>
            </div>
          </div>
        }
      >
        <Row gutter={[10, 10]} className="subscription-requests-modal__stats">
          {[
            ["En attente", stats.pending, <ClockCircleOutlined />, "pending"],
            ["Traitées", stats.processed, <CheckCircleOutlined />, "processed"],
            ["Refusées", stats.rejected, <CloseCircleOutlined />, "rejected"],
            ["Total", stats.total, <InboxOutlined />, "total"],
          ].map(([label, value, icon, tone]) => (
            <Col xs={12} lg={6} key={String(label)}>
              <div className={`subscription-requests-modal__stat is-${tone}`}>
                {icon as React.ReactNode}
                <div>
                  <strong>{formatSubscriptionNumber(Number(value))}</strong>
                  <span>{String(label)}</span>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        <div className="subscription-requests-modal__toolbar">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Rechercher une entreprise, un demandeur ou un plan…"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: "all", label: "Tous les statuts" },
              { value: "pending", label: "En attente" },
              { value: "processed", label: "Traitées" },
              { value: "rejected", label: "Refusées" },
              { value: "cancelled", label: "Annulées" },
            ]}
          />
          <Button
            icon={<ReloadOutlined spin={loading} />}
            onClick={() =>
              loadRequests(pagination.current_page, pagination.per_page)
            }
          >
            Actualiser
          </Button>
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={requests}
          loading={loading}
          pagination={false}
          scroll={{ x: 980 }}
          locale={{ emptyText: "Aucune demande ne correspond à ce filtre." }}
        />
        <div className="subscription-requests-modal__pagination">
          <Text type="secondary">
            {formatSubscriptionNumber(pagination.total)} demande(s)
          </Text>
          <Pagination
            current={pagination.current_page}
            total={pagination.total}
            pageSize={pagination.per_page}
            showSizeChanger
            onChange={loadRequests}
          />
        </div>
      </Modal>

      <Modal
        centered
        open={Boolean(selectedRequest)}
        onCancel={() => setSelectedRequest(null)}
        footer={null}
        width={640}
        title={
          <Space>
            {selectedRequest?.request_type === "plan_change" ? (
              <SwapOutlined />
            ) : (
              <ClockCircleOutlined />
            )}
            Examiner la demande
          </Space>
        }
      >
        {selectedRequest && (
          <div className="subscription-request-review">
            <div className="subscription-request-review__summary">
              <span>Entreprise</span>
              <strong>{selectedRequest.company_name}</strong>
              <span>Objet</span>
              <strong>
                {selectedRequest.request_type === "plan_change"
                  ? `${selectedRequest.current_plan_name || "Aucun plan"} → ${selectedRequest.requested_plan_name}`
                  : `Renouvellement de ${selectedRequest.requested_duration_months} mois`}
              </strong>
            </div>
            {selectedRequest.message && (
              <Paragraph className="subscription-request-review__message">
                {selectedRequest.message}
              </Paragraph>
            )}
            <Alert
              type="warning"
              showIcon
              message="Le traitement ne modifie pas automatiquement la souscription"
              description="Appliquez d'abord le changement ou le renouvellement depuis les actions de l'entreprise, puis marquez la demande comme traitée."
            />
            <Form form={form} layout="vertical">
              <Form.Item
                name="review_note"
                label="Réponse communiquée à l'abonné"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "Ajoutez une réponse pour assurer le suivi",
                  },
                ]}
              >
                <TextArea rows={4} maxLength={1000} showCount />
              </Form.Item>
            </Form>
            <div className="subscription-request-review__footer">
              <Button onClick={() => setSelectedRequest(null)}>Fermer</Button>
              <Button
                danger
                loading={reviewing}
                onClick={() => reviewRequest("rejected")}
              >
                Refuser
              </Button>
              <Button
                type="primary"
                loading={reviewing}
                onClick={() => reviewRequest("processed")}
              >
                Marquer comme traitée
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default SubscriptionRequestsModal;
